<script setup lang="ts">
import { useDisplay } from 'vuetify'
const { smAndDown } = useDisplay()
const { $api } = useNuxtApp()
const { formatSpeed } = useSpeedUnit()
const {
  userId = '', userIds = [], boatId = '', venueId = '', venueIds = [], windMin = null, windMax = null, ordering = '-scoring_start',
  showUser = true, showBoat = true, showLocation = true, showProgress = false,
} = defineProps<{
  userId?: string | number,
  // Extra sailors (user__in, combined with userId) — a coach viewing their own data plus one
  // or more students at once. Leave empty to filter by userId alone, as before.
  userIds?: (string | number)[],
  boatId?: string | number,
  venueId?: string | number,
  // Multiple venues (venue__in) — used together with, not instead of, venueId; leave venueId
  // unset when filtering by a set of venues.
  venueIds?: (string | number)[],
  windMin?: number | null,
  windMax?: number | null,
  ordering?: string,
  showUser?: boolean,
  showBoat?: boolean,
  showLocation?: boolean,
  // Adds wind/VMC/tacking columns pulled from already-computed per-activity analysis,
  // for the personal progress-over-time timeline.
  showProgress?: boolean,
}>()

const itemsPerPageRaces = ref(10)
const currentPage = ref(1)

const queryString = (page: number) =>
    `performance/activities/?user=${userIds.length ? '' : userId}&user__in=${userIds.length ? [userId, ...userIds].filter(Boolean).join(',') : ''}` +
    `&upload__boat__id=${boatId}&venue=${venueId}` +
    `&venue__in=${venueIds.join(',')}` +
    `&wind_min=${windMin ?? ''}&wind_max=${windMax ?? ''}` +
    `&ordering=${ordering}&page=${page}&page_size=${itemsPerPageRaces.value}` +
    (showProgress ? '&include_progress=1' : '')

const { data } = await useAsyncData(`activities-${userId}-${userIds.join(',')}-${boatId}-${venueId}-${venueIds.join(',')}-${windMin}-${windMax}-${ordering}`, async () => {
  return await $api(queryString(1))
})

const activities : Ref<Array<Activity>> = ref(data.value?.results)
const totalItemsRaces = ref(data.value?.count)

const headersRaces = [
  { title: 'Date', key: 'start_time' },
]
if(showUser){
  headersRaces.push({ title: 'Sailor', key: 'user_details' })
}
if(showBoat){
  headersRaces.push({ title: 'Boat', key: 'boat_details' })
}
if(showLocation){
  headersRaces.push({ title: 'Location', key: 'location' })
}
if(showProgress){
  headersRaces.push({ title: 'Wind', key: 'wind_speed_knots' })
  headersRaces.push({ title: 'Upwind VMG', key: 'upwind_analysis' })
  headersRaces.push({ title: 'Downwind VMG', key: 'downwind_analysis' })
  headersRaces.push({ title: 'Reach', key: 'reach_analysis' })
  headersRaces.push({ title: 'Tacking', key: 'tacking_summary' })
}
headersRaces.push({ title: 'Details', key: 'details' })

const loadingRaces: Ref<boolean> = ref(true)

const loadItems = async ({ page = 1 }) => {
  loadingRaces.value = true
  try {
    const r = await $api(queryString(page))
    activities.value = r.results
    totalItemsRaces.value = r.count
  }finally{
    loadingRaces.value = false
  }
}

// Filters are props, not internal table state — the table must react to them itself rather than
// relying on the parent remounting it via a :key (a component with an async setup, like this one,
// can briefly show its old and new instance at once across a remount during client-side
// navigation). Resets to page 1 since the current page may no longer exist under new filters.
watch(
    [() => userId, () => userIds.join(','), () => boatId, () => venueId, () => venueIds.join(','), () => windMin, () => windMax, () => ordering],
    () => {
      currentPage.value = 1
      loadItems({ page: 1 })
    },
)

</script>

<template>
  <v-data-table-server
      v-model:items-per-page="itemsPerPageRaces"
      v-model:page="currentPage"
      :headers="headersRaces"
      :items="activities"
      :items-length="totalItemsRaces"
      @update:options="loadItems"
      :mobile="smAndDown"
  >
    <template v-slot:item.start_time="{ item }">
      {{ niceDate(item.start_time) }}
      <v-chip
        v-if="item.visible === false"
        size="x-small"
        color="warning"
        variant="tonal"
        prepend-icon="mdi-eye-off"
        class="ml-2"
      >
        Hidden
      </v-chip>
    </template>
    <template v-slot:item.boat_details="{ value }">
      <nuxt-link v-if="value?.id" :to="`/boats/${value?.slug}-${value?.id}`">
        {{ value?.name }}
      </nuxt-link>
    </template>
    <template v-slot:item.location="{ item }">
      {{ item.venue_details?.name }}
    </template>
    <template v-slot:item.user_details="{ value }">
      <UserInTable
          :user="value"
          v-if="value"
      />
    </template>

    <template v-slot:item.wind_speed_knots="{ value }">
      <span v-if="value != null">{{ Math.round(value) }} kn</span>
      <span v-else class="text-medium-emphasis">-</span>
    </template>
    <template v-slot:item.upwind_analysis="{ item }">
      <span v-if="item.upwind_vmg_ms != null">{{ formatSpeed(item.upwind_vmg_ms) }}</span>
      <span v-else class="text-medium-emphasis">-</span>
    </template>
    <template v-slot:item.downwind_analysis="{ item }">
      <span v-if="item.downwind_vmg_ms != null">{{ formatSpeed(item.downwind_vmg_ms) }}</span>
      <span v-else class="text-medium-emphasis">-</span>
    </template>
    <template v-slot:item.reach_analysis="{ item }">
      <span v-if="item.reach_speed_ms != null">{{ formatSpeed(item.reach_speed_ms) }}</span>
      <span v-else class="text-medium-emphasis">-</span>
    </template>
    <template v-slot:item.tacking_summary="{ value }">
      <span v-if="value?.n_tacks">
        {{ value.n_tacks }} tacks<span v-if="value.avg_height_change_m != null"> · {{ value.avg_height_change_m }}&nbsp;m avg loss</span>
      </span>
      <span v-else class="text-medium-emphasis">-</span>
    </template>

    <template v-slot:[`item.details`]="{ item }">
      <nuxt-link
          :to="`/sails/${item.id}`"
          style="text-decoration: none !important;"
      >
        <v-icon start>mdi-magnify</v-icon>
      </nuxt-link>
      <nuxt-link :id="`sail-${item.id}`" :to="`/sails/${item.id}`">See details</nuxt-link>
    </template>
  </v-data-table-server>

</template>

<style scoped>

</style>
